import api from './api'

export interface InventoryOverview {
  totalProducts: number
  inStockProducts: number
  outOfStockProducts: number
  lowStockProducts: number
  lastSync: {
    provider: string
    status: string
    syncedAt: string
    processed: number
    created: number
    updated: number
  } | null
}

export interface GoogleSheetsConfig {
  sheetUrl?: string
  sheetGid?: string
  autoSyncEnabled?: boolean
  autoSyncMinutes?: number
  lastSyncedAt?: string | null
  lastSyncStatus?: string | null
  lastSyncError?: string | null
  webhookUrl?: string
}

export interface InventorySyncConfig {
  apiKey?: string
  strictStock?: boolean
  lowStockThreshold?: number
  googleSheets?: GoogleSheetsConfig
  outboundWebhookUrl?: string
  /** Solo viaja al backend cuando el negocio escribe uno nuevo; nunca se devuelve. */
  outboundWebhookSecret?: string
  outboundWebhookSecretSet?: boolean
  outboundSyncEnabled?: boolean
}

export interface InventoryItemResult {
  sku: string
  action: 'created' | 'updated' | 'skipped'
  reason?: string
  productId?: string
  name?: string
  stock?: number
  price?: number
}

export interface SyncResult {
  success: boolean
  provider: string
  processed: number
  created: number
  updated: number
  skipped: number
  errors: Array<{ sku: string; error: string }>
  summary: string
  items?: InventoryItemResult[]
}

export interface OutboundWebhookTestResult {
  success: boolean
  target: 'erp' | 'google_sheets'
  statusCode?: number
  durationMs: number
  responseBody?: string
  error?: string
  payloadPreview: Record<string, unknown>
  signature?: string | null
}

export const inventoryApi = {
  async getOverview(): Promise<InventoryOverview> {
    const response = await api.get('/inventory/overview')
    return response.data
  },

  async getConfig(): Promise<InventorySyncConfig> {
    const response = await api.get('/inventory/config')
    return response.data.config || {}
  },

  async updateConfig(
    config: Partial<InventorySyncConfig> & {
      generateNewApiKey?: boolean
      clearOutboundWebhookSecret?: boolean
    }
  ): Promise<InventorySyncConfig> {
    const response = await api.put('/inventory/config', config)
    return response.data.config
  },

  async testConnection(sheetUrl: string, sheetGid = '0'): Promise<SyncResult> {
    const response = await api.post('/inventory/sheets/sync', {
      sheetUrl,
      sheetGid,
      dryRun: true,
    })
    return response.data
  },

  async syncNow(options?: { sheetUrl?: string; sheetGid?: string; overwriteEnrichment?: boolean }): Promise<SyncResult> {
    const response = await api.post('/inventory/sheets/sync', {
      ...options,
      dryRun: false,
    })
    return response.data
  },

  async testWebhook(params: {
    target: 'erp' | 'google_sheets'
    webhookUrl: string
    secret?: string
  }): Promise<OutboundWebhookTestResult> {
    const response = await api.post('/inventory/webhooks/test', params)
    return response.data
  },

  async downloadSheetsTemplate(): Promise<Blob> {
    const response = await api.get('/inventory/sheets/template', {
      responseType: 'blob',
    })
    return response.data
  },
}
